import { Controller } from "@hotwired/stimulus"

/**
 * Keeps one weekly insight digest's Save AND Cancel buttons disabled until
 * that card's own form actually differs from the state it loaded with
 * (CONEK-7647 follow-up). Each digest is now posted independently, so both
 * buttons must reflect only ITS OWN dirtiness, never the other digest's —
 * and with nothing changed there is equally nothing to save or to discard.
 *
 * Also disables the cadence fields (day/hour/timezone/active) whenever
 * "Send me this digest" is unticked. The row IS the opt-in (see
 * Iot::InsightDigestSchedule) — a cadence has nowhere to live without a
 * subscribed row — so editing those fields while unsubscribed used to post
 * values the interactor silently drops (it only reads them on the `subscribe`
 * branch), while the controller still redirected with a success flash. The
 * page looked like it saved a cadence it never persisted, and a reload showed
 * the stale default. Disabling them removes the field from FormData entirely
 * (native HTML behaviour for a disabled control), so there is nothing
 * misleading left to submit.
 *
 * Snapshot-and-compare rather than per-field tracking, same idea as
 * iot--override-editor but generalised to a whole form: this one mixes
 * selects, checkboxes and their hidden `=0` companions (`include_hidden:
 * true`), a searchable Tom Select for timezone, and rows that render no
 * controls at all (blocked / not entitled) — comparing the whole serialized
 * form sidesteps special-casing each field type, and Tom Select forwards a
 * native `change` on the original `<select>` it wraps, so it bubbles here
 * like any other field.
 *
 * Cancel is a plain `<button type="button">`, not an `<a>`: an anchor's
 * `disabled` attribute does nothing in HTML (links aren't form controls), so
 * disabling it would need a second, JS-only mechanism anyway. A real button
 * gets the native disabled behaviour (and Ui::ButtonComponent's
 * `disabled:opacity-50` styling) for free, and `cancel()` below does the
 * navigation only when it is actually enabled.
 *
 * Markup contract:
 *   <form data-controller="iot--digest-form"
 *         data-iot--digest-form-cancel-url-value="/path/back"
 *         data-action="input->iot--digest-form#check change->iot--digest-form#check">
 *     <input type="checkbox" data-iot--digest-form-target="subscribe"
 *            data-action="change->iot--digest-form#toggleCadenceFields">
 *     <select data-iot--digest-form-target="dependentField">…</select>
 *     <input type="checkbox" data-iot--digest-form-target="dependentField">
 *     <button data-iot--digest-form-target="cancel"
 *             data-action="click->iot--digest-form#cancel" type="button">Cancel</button>
 *     <button data-iot--digest-form-target="submit" type="submit">Save</button>
 *   </form>
 */
export default class extends Controller {
  static targets = ["submit", "cancel", "subscribe", "dependentField"]
  static values = { cancelUrl: String }

  connect() {
    // Order matters: this corrects the server-rendered state for a
    // never-subscribed row (the selects render enabled — same
    // `disabled: !actionable` as every other field — with nothing yet tying
    // them to "Send me this digest") BEFORE the dirty-check snapshot is
    // taken. A disabled control is excluded from FormData entirely, so
    // snapshotting first and disabling after would make the very act of
    // loading the page look like an edit — Save/Cancel would enable
    // themselves on a page nobody touched.
    this._syncCadenceFields()
    this.initialSnapshot = this._snapshot()
    this._setDisabled(true)
  }

  check() {
    this._setDisabled(this._snapshot() === this.initialSnapshot)
  }

  toggleCadenceFields() {
    this._syncCadenceFields()
    this.check()
  }

  _syncCadenceFields() {
    if (!this.hasSubscribeTarget) return
    const enabled = this.subscribeTarget.checked
    for (const field of this.dependentFieldTargets) {
      const select = this._tomSelect(field)
      if (select) {
        if (enabled) {
          select.enable()
        } else {
          select.disable()
        }
      } else {
        field.disabled = !enabled
      }
    }
  }

  // Guards against a disabled-but-still-clicked button (keyboard Enter on a
  // focused disabled element can still fire a click in some browsers) — the
  // button being disabled means there is nothing to discard, so navigating
  // away would just be an unprompted, pointless page reload.
  cancel() {
    if (this.hasCancelTarget && this.cancelTarget.disabled) return
    window.location.href = this.cancelUrlValue
  }

  _snapshot() {
    return new URLSearchParams(new FormData(this.element)).toString()
  }

  // Ui::ButtonComponent renders `disabled` and `aria-disabled` together (see
  // iot--override-editor), so flip both to keep assistive tech in step.
  _setDisabled(flag) {
    for (const target of [this.submitTargets, this.cancelTargets].flat()) {
      target.disabled = flag
      target.setAttribute("aria-disabled", String(flag))
    }
  }

  // ui--select wraps a plain <select> with its own custom-rendered control,
  // which does not react to the native element's `disabled` property on its
  // own (confirmed in tom-select's own `sync()` — it is read-only-at-call-time,
  // not observed). The component's sanctioned door for an outside controller
  // is the `ui--select:set-options` event (see its `setOptions` doc comment),
  // but that is built for swapping the option list, not for disabling a select
  // that still has a full, unchanged list of options — forcing that through
  // `set-options` would mean reconstructing and reapplying every option just
  // to flip one flag. TomSelect's own `disable()`/`enable()` are its public,
  // documented API (not private state), so calling them via the controller's
  // `tomSelect` instance is the narrower, more honest choice here. A plain
  // field (the Active checkbox — no `ui--select` controller on it) returns
  // null and falls back to the native `.disabled` setter instead.
  _tomSelect(field) {
    const wrapper = field.closest('[data-controller~="ui--select"]')
    if (!wrapper) return null
    const controller = this.application.getControllerForElementAndIdentifier(
      wrapper,
      "ui--select",
    )
    return controller?.tomSelect ?? null
  }
}
